import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Custom hook to fetch role templates for a company
 * Includes course counts and connected user counts
 *
 * @param {string} companyId - The company ID
 * @param {object} options - Configuration options
 * @param {boolean} options.enabled - Whether to fetch data
 * @returns {object} { templates, loading, error, refetch }
 */
export const useRoleTemplates = (companyId, options = {}) => {
  const { enabled = true } = options;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!companyId || !enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch role templates with course data
      const { data: templatesData, error: templatesError } = await supabase
        .from('role_templates')
        .select(`
          *,
          role_template_requirements (
            id,
            course_name,
            course_link_id,
            course_link_type
          )
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (templatesError) throw templatesError;

      // For each template, count distinct users connected via compliance_assignments
      const enriched = await Promise.all(
        (templatesData || []).map(async (template) => {
          const { count, error: countError } = await supabase
            .from('user_role_templates')
            .select('user_id', { count: 'exact', head: true })
            .eq('role_template_id', template.id);

          return {
            ...template,
            requirementCount: template.role_template_requirements?.length || 0,
            requirements: (template.role_template_requirements || []).map(rtr => ({
              course_name: rtr.course_name,
              course_link_id: rtr.course_link_id,
              course_link_type: rtr.course_link_type,
              name: rtr.course_name
            })),
            connectedUsers: countError ? 0 : (count || 0)
          };
        })
      );

      setTemplates(enriched);
    } catch (err) {
      console.error('Error fetching role templates:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    templates,
    loading,
    error,
    refetch: fetchData
  };
};
